import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import apiOffers from 'api/offers';
import IOfferNda, { IOfferNdaUser } from 'api/interfaces/offers/IOfferNda';
import UserSelect from 'components/layout/forms/UserSelect';
import translations from 'services/translations/translations';
import {
  ALL_CONTENT,
  NDA_PROTECTIONS,
} from 'services/enums/offers/ndaProtections';
import { addMessage } from 'store/messages/actions';

import 'style/layout/modal/ModalConfirm.css';
import 'style/offers/OfferNda.css';

const getUserName = (user: IOfferNdaUser | null) =>
  user === null ? '' : `${user.firstName} ${user.lastName}`.trim();

/**
 * Defines which parts of the offer are NDA protected and who may see them. Only the NDA
 * admin (the person in whose name the offer is made) and the NDA maintainers can change it.
 */
const OfferNdaModal = ({
  isOpen,
  nda,
  ndaOfferId,
  onSaved,
  readOnly,
  toggle,
}: {
  isOpen: boolean;
  nda: IOfferNda;
  // The offer whose NDA is edited. Additional offers use the NDA of their offer.
  ndaOfferId: number | undefined;
  onSaved: (nda: IOfferNda) => void;
  // Only the right to read the offer: the NDA cannot be saved either
  readOnly?: boolean;
  toggle: () => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);

  const [protections, setProtections] = useState<string[]>(nda.protections);
  const [members, setMembers] = useState<IOfferNdaUser[]>(nda.members);
  const [admin, setAdmin] = useState<IOfferNdaUser | null>(nda.admin);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setProtections(nda.protections);
      setMembers(nda.members);
      setAdmin(nda.admin);
    }
  }, [isOpen, nda]);

  // Only the program admin (NDA maintenance permission) can change the NDA admin
  const changeAdmin = (option: { label: string; value: string } | null) => {
    if (option === null || option.value === '') {
      return;
    }

    const [firstName, ...lastNames] = option.label.split(' ');
    setAdmin({ firstName, id: option.value, lastName: lastNames.join(' ') });
    setMembers(members.filter((member) => member.id !== option.value));
  };

  const editable = nda.canManage && ndaOfferId !== undefined && !readOnly;
  const allContent = protections.includes(ALL_CONTENT);

  const toggleProtection = (protection: string) => {
    setProtections(
      protections.includes(protection)
        ? protections.filter((existing) => existing !== protection)
        : [...protections, protection]
    );
  };

  const addMember = (option: { label: string; value: string } | null) => {
    if (
      option === null ||
      option.value === '' ||
      option.value === admin?.id ||
      members.some((member) => member.id === option.value)
    ) {
      return;
    }

    const [firstName, ...lastNames] = option.label.split(' ');
    setMembers([
      ...members,
      { firstName, id: option.value, lastName: lastNames.join(' ') },
    ]);
  };

  const removeMember = (id: string) =>
    setMembers(members.filter((member) => member.id !== id));

  const save = async () => {
    if (!editable || ndaOfferId === undefined || saving) {
      return;
    }

    setSaving(true);

    try {
      await apiOffers.putNda(ndaOfferId, {
        adminUserId: nda.canChangeAdmin ? admin?.id : null,
        memberUserIds: members.map((member) => member.id),
        protections,
      });
      onSaved({ ...nda, admin, members, protections });
      dispatch(addMessage(t(`${translations.offers}:nda.Saved`)));
      toggle();
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:nda.Save failed`)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader className="modal-header" toggle={toggle}>
        {t(`${translations.offers}:nda.Title`)}
      </ModalHeader>
      <ModalBody>
        <p className="offer-nda-admin">
          {t(`${translations.offers}:nda.Admin`)}:{' '}
          <strong>{getUserName(admin)}</strong>
        </p>
        {editable && nda.canChangeAdmin ? (
          <UserSelect
            label={t(`${translations.offers}:nda.Change admin`)}
            onChange={changeAdmin as any}
            value={{ label: '', value: '' }}
          />
        ) : null}
        {editable ? null : (
          <p className="offer-nda-info">
            {t(
              `${translations.offers}:${
                ndaOfferId === undefined
                  ? 'nda.Managed on offer'
                  : 'nda.Only admin can change'
              }`
            )}
          </p>
        )}

        <h6>{t(`${translations.offers}:nda.Protected information`)}</h6>
        {NDA_PROTECTIONS.map((protection) => {
          const coveredByAll = allContent && protection !== ALL_CONTENT;

          return (
            <FormGroup check key={protection}>
              <Label check>
                <Input
                  checked={coveredByAll || protections.includes(protection)}
                  disabled={!editable || coveredByAll}
                  onChange={() => toggleProtection(protection)}
                  type="checkbox"
                />{' '}
                {t(`${translations.offers}:nda.protections.${protection}`)}
              </Label>
            </FormGroup>
          );
        })}

        {nda.hasAccess ? (
          <>
            <h6 className="mt-3">{t(`${translations.offers}:nda.Users`)}</h6>
            <ul className="offer-nda-members">
              <li>
                {getUserName(admin)}{' '}
                <span className="offer-nda-role">
                  ({t(`${translations.offers}:nda.Admin`)})
                </span>
              </li>
              {members.map((member) => (
                <li key={member.id}>
                  {getUserName(member)}
                  {editable ? (
                    <Button
                      className="modal-button offer-nda-remove"
                      onClick={() => removeMember(member.id)}
                      size="sm"
                    >
                      {t(`${translations.common}:Delete`)}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            {editable ? (
              <UserSelect
                label={t(`${translations.offers}:nda.Add user`)}
                onChange={addMember as any}
                value={{ label: '', value: '' }}
              />
            ) : null}
          </>
        ) : (
          <p className="offer-nda-info">
            {t(`${translations.offers}:nda.No access`)}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        {editable ? (
          <Button className="modal-button" disabled={saving} onClick={save}>
            {t(`${translations.common}:Save`)}
          </Button>
        ) : null}
        <Button onClick={toggle} className="modal-button">
          {t(`${translations.offers}:Close`)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default OfferNdaModal;
